import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { OverviewFieldRow } from "~/features/settings/components/OverviewFieldRow";
import {
  settingsOverviewFormControlMaxWidthPx,
  settingsSelectSx
} from "~/features/settings/settings.styles";
import { workstationRoles } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useGetWorkstationsQuery,
  usePostCreateWorkstationMutation
} from "~/redux/warehouse/workstation.hooks";
import {
  AutostoreGridDto,
  CreateWorkstation,
  WorkstationPortRequestDto,
  WorkstationSummaryDto
} from "~/types/api";

import { AddWorkstationButton } from "./AddWorkstationButton";
import { AutostoreGridSelector } from "./AutostoreGridSelector";
import { AutostorePortSelector } from "./AutostorePortSelector";

const overviewActionsColumnGap = "84px";

const NEW_ID_DEVICE_PATTERN = /^new id (\d+)$/i;

function defaultDeviceIdForCreate(
  workstations: WorkstationSummaryDto[] | undefined
): string {
  const list = workstations ?? [];
  const taken = new Set<number>();
  for (const ws of list) {
    const match = ws.deviceId?.match(NEW_ID_DEVICE_PATTERN);
    if (match) {
      taken.add(parseInt(match[1], 10));
    }
  }
  let n = 1;
  while (taken.has(n)) {
    n += 1;
  }
  return `new id ${n}`;
}

export function AddWorkstation() {
  const { t } = useTranslation();
  const [createWorkstationAutostoreGrid, setCreateWorkstationAutostoreGrid] =
    useState<AutostoreGridDto | null>(null);
  const [createWorkstationPortId, setCreateWorkstationPortId] = useState<
    number[]
  >([]);
  const [unusedPorts, setUnusedPorts] = useState<number[]>([]);
  const { data: workstations, refetch: refetchWorkstations } =
    useGetWorkstationsQuery();

  const { data: autostoreGrids } = useGetAutostoreGridsQuery();
  const [postCreateWorkstation, { isLoading: isCreatingWorkstation }] =
    usePostCreateWorkstationMutation();
  const { errorToast, successToast } = useToast();

  const addPort = () => {
    const selectedIds = createWorkstationPortId ?? [];
    const newPortId = unusedPorts.find((port) => !selectedIds.includes(port));
    if (newPortId === undefined) {
      return;
    }
    setCreateWorkstationPortId([...selectedIds, newPortId]);
  };

  const updatePort = (index: number, newPortId: number) => {
    if (createWorkstationPortId) {
      const updatedPorts = [...createWorkstationPortId];
      updatedPorts[index] = newPortId;
      setCreateWorkstationPortId(updatedPorts);
    }
  };

  const removePort = (index: number) => {
    if (createWorkstationPortId) {
      const updatedPorts = createWorkstationPortId.filter(
        (_, portIndex) => index !== portIndex
      );
      setCreateWorkstationPortId(updatedPorts);
    }
  };

  const handleSelectAutostoreGrid = (selectedGridId: string | undefined) => {
    const selectedGrid =
      autostoreGrids?.find((grid) => grid.autostoreGridId === selectedGridId) ||
      undefined;
    if (selectedGrid) {
      setCreateWorkstationAutostoreGrid(selectedGrid);
      setCreateWorkstationPortId([]);
    }
  };

  const handleAddWorkstation = async () => {
    if (!createWorkstationAutostoreGrid || !createWorkstationPortId.length) {
      return;
    }

    const ports: WorkstationPortRequestDto[] = createWorkstationPortId.map(
      (portId, index) => ({
        binSize: 330,
        configuration: "independent",
        portId: portId,
        coordinate: { x: 0, y: index },
        inductionBinConfiguration: ["Whole Bin"],
        cleaningDirection: "No Direction",
        cells: [{ x: 0, y: 0 }]
      })
    );

    const newWorkstation: CreateWorkstation = {
      autostoreGridId: createWorkstationAutostoreGrid.autostoreGridId,
      deviceId: defaultDeviceIdForCreate(workstations),
      ports,
      totePlacements: [],
      mode: "",
      multiPortEnabled: false,
      orientation: "North",
      roles: workstationRoles,
      pickableOrderTypes: []
    };

    try {
      await postCreateWorkstation(newWorkstation).unwrap();
      await refetchWorkstations();
      successToast(t("workstation added"));
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  useEffect(() => {
    if (!autostoreGrids?.length) {
      return;
    }

    const currentId = createWorkstationAutostoreGrid?.autostoreGridId;
    const currentStillValid =
      currentId != null &&
      autostoreGrids.some((grid) => grid.autostoreGridId === currentId);

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (currentStillValid) {
      return;
    }

    const nextGrid = autostoreGrids[0];
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
    setCreateWorkstationAutostoreGrid(nextGrid);
    const initialPortId = nextGrid.autostorePorts[0]?.portId;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
    setCreateWorkstationPortId(initialPortId != null ? [initialPortId] : []);
  }, [autostoreGrids, createWorkstationAutostoreGrid]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!createWorkstationAutostoreGrid || !autostoreGrids || !workstations)
      return;

    const portIsUsedByAnyWorkstation = (portId: number) => {
      return !!workstations.find(
        (workstation) =>
          workstation.ports.length > 0 &&
          workstation.autostoreGridId ===
            createWorkstationAutostoreGrid.autostoreGridId &&
          workstation.ports.some(
            (port) => port.portId === portId || port.parentPortId === portId
          )
      );
    };

    const currentGridPorts = createWorkstationAutostoreGrid.autostorePorts.map(
      (port) => port.portId
    );
    const unassignedPorts = currentGridPorts.filter(
      (portId) => !portIsUsedByAnyWorkstation(portId)
    );

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setUnusedPorts(unassignedPorts);

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setCreateWorkstationPortId((prev) => {
      if (unassignedPorts.length === 0) {
        return [];
      }
      const stillValid = prev.filter((id) => unassignedPorts.includes(id));
      if (stillValid.length > 0) {
        return stillValid;
      }
      return [unassignedPorts[0]];
    });
  }, [createWorkstationAutostoreGrid, autostoreGrids, workstations]);

  const selectedGridHasUnusedPorts = unusedPorts.length > 0;

  return (
    <Box
      id="create-workstation-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0
      }}
    >
      <Stack id="create-workstation-body" spacing={3} sx={{ flex: "1 1 auto" }}>
        <Stack id="create-workstation-free-ports-block" spacing={1.5}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("create workstation free ports label")}
          </Typography>
          {createWorkstationAutostoreGrid ? (
            selectedGridHasUnusedPorts ? (
              <Stack
                id="create-workstation-free-ports-chips"
                direction="row"
                flexWrap="wrap"
                gap={1}
                role="list"
              >
                {unusedPorts.map((pid) => (
                  <Chip
                    key={pid}
                    label={`#${pid}`}
                    size="small"
                    variant="outlined"
                    role="listitem"
                  />
                ))}
              </Stack>
            ) : (
              <Alert severity="info" variant="outlined" role="status">
                <AlertTitle>
                  {t("create workstation no free ports title")}
                </AlertTitle>
                {t("create workstation no free ports detail")}
              </Alert>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("create workstation pick grid for ports hint")}
            </Typography>
          )}
        </Stack>

        <Box
          id="create-workstation-fields-grid"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) minmax(0, 1fr)"
            },
            columnGap: { xs: 0, md: overviewActionsColumnGap },
            rowGap: { xs: 2, md: 2 },
            alignItems: "start",
            width: "100%",
            minWidth: 0
          }}
        >
          <Box
            id="create-workstation-grid-field-cell"
            sx={{ gridColumn: { xs: 1, md: "1 / -1" }, minWidth: 0 }}
          >
            <OverviewFieldRow
              id="create-workstation-grid-row"
              labelId="create-ws-grid-label"
              label={t("autostore grid")}
              helperText={t("create workstation grid helper")}
            >
              <Box
                id="create-workstation-grid-select-wrap"
                sx={{
                  width: "100%",
                  maxWidth: {
                    xs: "100%",
                    sm: Math.round(settingsOverviewFormControlMaxWidthPx * 0.75)
                  },
                  minWidth: 0
                }}
              >
                <AutostoreGridSelector
                  selectedGrid={createWorkstationAutostoreGrid?.autostoreGridId}
                  onSelect={handleSelectAutostoreGrid}
                  hideLabel
                  variant="outlined"
                  selectSx={settingsSelectSx}
                  externalLabelId="create-ws-grid-label"
                />
              </Box>
            </OverviewFieldRow>
          </Box>

          <Box id="create-workstation-ports-field-cell" sx={{ minWidth: 0 }}>
            {selectedGridHasUnusedPorts ? (
              <OverviewFieldRow
                id="create-workstation-ports-row"
                labelId="create-ws-ports-label"
                label={t("ports")}
                helperText={t("create workstation ports helper")}
                helperTextNoWrap
              >
                <AutostorePortSelector
                  ports={createWorkstationPortId}
                  unusedPorts={unusedPorts}
                  addPort={addPort}
                  updatePort={updatePort}
                  removePort={removePort}
                  hideLabel
                  variant="outlined"
                  selectSx={settingsSelectSx}
                />
              </OverviewFieldRow>
            ) : (
              <OverviewFieldRow
                id="create-workstation-ports-row-empty"
                labelId="create-ws-ports-label"
                label={t("ports")}
                helperText={t("create workstation no free ports detail")}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("create workstation no free ports title")}
                </Typography>
              </OverviewFieldRow>
            )}
          </Box>
        </Box>
      </Stack>

      <Box
        id="create-workstation-footer"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          width: "100%",
          pt: 2,
          mt: "auto"
        }}
      >
        {!selectedGridHasUnusedPorts ? (
          <Tooltip
            title={t("create workstation no free ports detail")}
            placement="top"
            arrow
          >
            <Box
              id="create-workstation-submit-wrap"
              component="span"
              sx={{ display: "inline-flex", justifyContent: "flex-end" }}
            >
              <AddWorkstationButton
                addWorkstation={handleAddWorkstation}
                isCreating={isCreatingWorkstation}
                disabled
              />
            </Box>
          </Tooltip>
        ) : (
          <Box
            id="create-workstation-submit-wrap"
            sx={{ display: "inline-flex", justifyContent: "flex-end" }}
          >
            <AddWorkstationButton
              addWorkstation={handleAddWorkstation}
              isCreating={isCreatingWorkstation}
              disabled={false}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
